import { useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { AuthProvider as OidcProvider, useAuth } from 'react-oidc-context';

import { useReadCurrentUser } from '@/lib/api/generated/api';
import { setAccessToken, setTokenRenewer } from '@/lib/auth/tokenStore';
import { oidcConfig } from '@/lib/auth/oidcConfig';
import { SessionContextProvider, type SessionUser } from '@/lib/auth/session';
import { handDraftsOver } from '@/shared/store/personalDrafts';

/**
 * Bridges the OIDC library to the rest of the app.
 *
 * Two things cross the boundary here and nowhere else: the access token, pushed
 * into the module-level store the request seam reads, and the signed-in person,
 * read back from the API rather than from the token's claims — `/api/users/me`
 * returns the `app_user` row, and its `id` is what every foreign key in the
 * product points at. The token's `sub` is the provider's identifier and means
 * nothing to the rest of the schema.
 */
function SessionBridge({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const accessToken = auth.user?.access_token ?? null;

  useEffect(() => {
    setAccessToken(accessToken);
  }, [accessToken]);

  useEffect(() => {
    setTokenRenewer(async () => {
      try {
        const renewed = await auth.signinSilent();
        const token = renewed?.access_token ?? null;
        setAccessToken(token);
        return token;
      } catch {
        // Say so rather than redirect silently. Whatever the person was doing is
        // still on screen — a half-written task, an unsaved rename — and bouncing
        // them to a sign-in page would discard it without ever telling them it
        // did not go through (spec 067, "session expiry mid-action").
        setAccessToken(null);
        toast.error('Your session ended. Sign in again to save this.', {
          duration: Infinity,
          action: {
            label: 'Sign in',
            onClick: () => void auth.signinRedirect(),
          },
        });
        return null;
      }
    });
    return () => setTokenRenewer(null);
  }, [auth]);

  // Only asked once there is a token to ask with; otherwise every signed-out
  // render would fire a request that can only 401.
  const { data } = useReadCurrentUser({
    query: { enabled: Boolean(accessToken), staleTime: 5 * 60 * 1000 },
  });

  const user: SessionUser | null = useMemo(() => {
    if (!data || data.status !== 200) return null;
    return {
      id: data.data.id,
      email: data.data.email,
      displayName: data.data.display_name,
      roles: data.data.roles,
    };
  }, [data]);

  // The person is known: drafts someone else left in this browser go (spec
  // 197). Keyed on the id, so a re-read of the same person changes nothing.
  const userId = user?.id ?? null;
  useEffect(() => {
    if (userId !== null) handDraftsOver(userId);
  }, [userId]);

  /*
    Signing out goes through a ref rather than being closed over directly.

    `react-oidc-context` hands back a fresh `auth` object on every internal tick —
    a silent renew, an `activeNavigator` change — so depending on it here rebuilt
    this context value, and with it re-rendered every `useSession()` consumer in
    the app, on ticks that changed nothing any of them can see.

    Assigned in an effect, never during render: a ref written while rendering is
    not safe under concurrent rendering, and the value is only ever read from an
    event handler, which always runs after the effect has committed.
  */
  const signOutRef = useRef<() => void>(() => {});
  const switchAccountRef = useRef<() => void>(() => {});
  useEffect(() => {
    signOutRef.current = () => {
      setAccessToken(null);
      void auth.signoutRedirect();
    };
    /*
      Signed out at the identity provider, then back to this same page: whoever
      signs in next may well be the one who can open it. Arriving signed out,
      `RequireAuth` signs in again with the page as its `returnTo`, and with the
      provider's own session ended it shows its form, so another account can be
      entered. `prompt=login` alone does not do it: Keycloak refuses a different
      person while its session still remembers this one ("You are already
      authenticated as different user"). The realm accepts any path on this
      origin as a post-logout address.
    */
    switchAccountRef.current = () => {
      const back = `${window.location.origin}${window.location.pathname}${window.location.search}`;
      setAccessToken(null);
      void auth.signoutRedirect({ post_logout_redirect_uri: back });
    };
  });

  const value = useMemo(
    () => ({
      user,
      signOut: () => {
        signOutRef.current();
      },
      switchAccount: () => {
        switchAccountRef.current();
      },
    }),
    [user],
  );

  return (
    <SessionContextProvider value={value}>{children}</SessionContextProvider>
  );
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  return (
    <OidcProvider {...oidcConfig}>
      <SessionBridge>{children}</SessionBridge>
    </OidcProvider>
  );
}
